import { type Either, left, right } from 'fp-ts/Either';
import { ZodError, z } from 'zod';

import { TypedError, typedError } from './typedError';

export function getFormValues<Z extends z.ZodObject<z.ZodRawShape>>(
  form: HTMLFormElement,
  expectedData: Z,
): Either<TypedError<'InvalidFormData'>, z.output<Z>> {
  const formData = new FormData(form);
  const entries = Array.from(formData.entries());

  const result = entries.reduce(
    (acc, cur) => {
      const [key, value] = cur;

      if (value instanceof File && value.size === 0 && value.name === '') {
        return acc;
      }

      if (
        typeof value === 'string' &&
        value.startsWith('[') &&
        value.endsWith(']')
      ) {
        try {
          const parsed = JSON.parse(value);
          if (Array.isArray(parsed)) {
            acc[key] = parsed;
            return acc;
          }
        } catch {}
      }

      if (key in acc) {
        if (Array.isArray(acc[key])) {
          acc[key].push(value);
        } else {
          acc[key] = [acc[key], value];
        }
      } else {
        acc[key] = value;
      }

      return acc;
    },
    {} as { [param: string]: unknown },
  );

  try {
    const shape = expectedData.shape;
    const processedResult = { ...result };

    for (const [key, schema] of Object.entries(shape)) {
      if (schema instanceof z.ZodArray) {
        if (key in processedResult && !Array.isArray(processedResult[key])) {
          processedResult[key] = [processedResult[key]];
        } else if (!(key in processedResult)) {
          processedResult[key] = [];
        }
      }
    }

    return right(expectedData.parse(processedResult));
  } catch (e) {
    if (e instanceof ZodError) {
      const firstError = e.issues[0];
      if (!firstError) {
        return left(typedError('InvalidFormData', 'Invalid form data'));
      }
      let errorMessage = firstError.message;

      if (firstError.path.length > 0) {
        const fieldName = firstError.path.join('.');
        errorMessage = `${fieldName}: ${errorMessage}`;
      }

      return left(typedError('InvalidFormData', errorMessage));
    }

    return left(typedError('InvalidFormData', String(e)));
  }
}
